import { KatapultElement, html } from '../../mixins/katapult-element';
import { KatapultEditorTemplate } from './katapult-editor-template';
import { NameToKey } from '../../modules/NameToKey.js';
import { CamelCase } from '../../modules/CamelCase.js';
import { Path } from '../../modules/Path.js';
import { IllegalCharacters, ContainsIllegalCharacters } from '../../modules/ContainsIllegalCharacters.js';
import { GetNewAttributeValue } from '../../modules/GetNewAttributeValue.js';
import { ToArray } from '../../modules/ToArray.js';
import '../katapult-elements/katapult-button.js';
import './tags/model-item-tag-selector.js';

class KatapultAttributeEditor extends KatapultEditorTemplate {
  static get template() {
    return html`
      ${super.style}
      <style>
        .body {
          margin-left: 48px;
        }
        paper-tabs {
          --paper-tabs-selection-bar-color: var(--secondary-color);
          --paper-tab-ink: var(--secondary-color);
        }
        paper-tab {
          --paper-tab: {
            font-size: 12pt;
            font-weight: normal;
          };
          --paper-tab-content-focused: {
            font-weight: normal;
          };
        }

        paper-header-content {
          background-color: white;
          color: var(--paper-grey-800);
        }
        paper-header-content paper-input {
          text-transform: none;
        }

        [drag-handle]:hover {
          cursor: move;
        }

        paper-tabs {
          --paper-tabs-content: {
            display: flex;
            min-width: 100%;
          };
        }

        .dialog-option {
          min-width: 5.14em;
        }

        .input-label[disabled] {
          opacity: 0.65;
        }

        .validation-regex-builder {
          max-width: 650px;
          background-color: var(--paper-grey-50);
          border: 1px solid rgba(0 0 0 / 6%);
          padding: 16px;
          display: flex;
          flex-direction: column;
          gap: 16px;
          border-radius: 16px;
        }
      </style>
      <katapult-firebase-worker
        id="attributeGroupsFirebaseWorker"
        path="[[modelSpaceDetails.path]]/models/attribute_groups"
        data="{{attributeGroups}}"
      >
      </katapult-firebase-worker>
      <katapult-firebase-worker path="[[modelSpaceDetails.path]]/models/attributes" data="{{otherAttributes}}"> </katapult-firebase-worker>

      <paper-dialog
        id="editAttributeGroups"
        entry-animation="scale-up-animation"
        exit-animation="fade-out-animation"
        no-cancel-on-outside-click=""
      >
        <div title secondary-color>Edit Attribute Groups</div>
        <div body>
          <paper-table style="overflow: hidden;" white outline rounded dividers>
            <paper-table-scroll on-sort-changed="attributeGroupsArraySortChanged">
              <template is="dom-repeat" items="[[attributeGroupsArray]]">
                <paper-row slot="sortable" item="[[item]]">
                  <paper-cell style="background-color:white;">
                    <iron-icon icon="katapult-misc:drag-indicator"></iron-icon>
                    <div style="flex-grow:1; white-space: nowrap;">[[item.$key]]</div>
                    <katapult-button iconOnly noBorder icon="delete" on-click="removeAttributeGroup"></katapult-button>
                  </paper-cell>
                </paper-row>
              </template>
            </paper-table-scroll>
          </paper-table>
          <paper-row>
            <paper-cell>
              <paper-input
                id="newAttributeGroupNameInput"
                label="New Attribute Group"
                value="{{newAttributeGroupName}}"
                autofocus
                no-label-float
              ></paper-input>
            </paper-cell>
            <paper-cell icon-button>
              <katapult-button
                iconOnly
                noBorder
                color="var(--secondary-color)"
                icon="add"
                on-click="addNewAttributeGroup"
                style="border-radius: 999px"
              ></katapult-button>
            </paper-cell>
          </paper-row>
        </div>
        <div buttons>
          <katapult-button dialog-dismiss>Close</katapult-button>
        </div>
      </paper-dialog>

      <paper-dialog id="createPicklistDialog" fit-into="[[$this]]" entry-animation="scale-up-animation" exit-animation="fade-out-animation">
        <div title secondary-color>
          <span>New Picklist</span>
          <iron-icon icon="av:playlist-add"></iron-icon>
        </div>
        <div body>
          <paper-input label="New Picklist Name" value="{{newPicklistName}}"></paper-input>
        </div>
        <div buttons>
          <katapult-button dialog-dismiss>Close</katapult-button>
          <div style="flex-grow: 1;"></div>
          <katapult-button color="var(--secondary-color)" on-click="createPicklistButtonClicked" disabled="[[!sanitizedPicklistName]]"
            >Create Picklist</katapult-button
          >
        </div>
      </paper-dialog>

      <template is="dom-repeat" items="[[steps]]" as="step">
        <katapult-editor-step
          step$="[[step.id]]"
          creating-item="[[creatingItem]]"
          active="[[step.active]]"
          index="[[step.index]]"
          current-index="[[currentIndex]]"
          title="[[step.title]]"
        >
          <template is="dom-if" if="[[equal(step.id, 'editability')]]" restamp>
            <paper-table light-grey style="width: 400px;" rounded outline slot="body">
              <paper-row input>
                <paper-cell>
                  <katapult-drop-down
                    items="[[editabilityOptions]]"
                    label-path="label"
                    value-path="value"
                    label="Select a Restriction"
                    value="{{_itemData.editability}}"
                    null-value="[[null]]"
                    no-label-float
                  ></katapult-drop-down>
                </paper-cell>
              </paper-row>
            </paper-table>
            <katapult-button slot="current-step-body" on-click="nextStep" class="dialog-option" style="margin-left: 16px"
              >Skip</katapult-button
            >
          </template>

          <template is="dom-if" if="[[equal(step.id, 'input_type')]]" restamp>
            <paper-table light-grey style="width: 400px;" rounded outline slot="body">
              <paper-row input>
                <paper-cell>
                  <katapult-drop-down
                    items="[[inputTypes]]"
                    label-path="$val"
                    value-path="$key"
                    label="Select an Input Type"
                    value="{{_itemData.gui_element}}"
                    disabled="[[!fullyEditable]]"
                    no-label-float
                  ></katapult-drop-down>
                </paper-cell>
              </paper-row>
            </paper-table>
          </template>

          <template is="dom-if" if="[[equal(step.id, 'placeholder')]]" restamp>
            <paper-table light-grey style="width: 400px;" rounded outline slot="body">
              <paper-row input>
                <paper-cell>
                  <paper-input
                    label="Placeholder Text"
                    value="{{_itemData.placeholder}}"
                    no-label-float
                    disabled="[[!fullyEditable]]"
                  ></paper-input>
                </paper-cell>
              </paper-row>
            </paper-table>
            <katapult-button slot="current-step-body" on-click="nextStep" class="dialog-option" style="margin-left: 16px"
              >Skip</katapult-button
            >
          </template>

          <template is="dom-if" if="[[equal(step.id, 'may_exist_on')]]" restamp>
            <div style="display: flex; flex-grow: 1; justify-content: space-between; max-width: 600px;" slot="body">
              <paper-checkbox disabled="[[!fullyEditable]]" checked="{{attributeTypes.node}}">Nodes</paper-checkbox>
              <paper-checkbox disabled="[[!fullyEditable]]" checked="{{attributeTypes.connection}}">Connections</paper-checkbox>
              <paper-checkbox disabled="[[!fullyEditable]]" checked="{{attributeTypes.section}}">Sections</paper-checkbox>
              <paper-checkbox disabled="[[!fullyEditable]]" checked="{{attributeTypes.photo}}">Photo Annotations</paper-checkbox>
              <paper-checkbox disabled="[[!fullyEditable]]" checked="{{attributeTypes.job}}">Jobs</paper-checkbox>
            </div>
          </template>

          <template is="dom-if" if="[[equal(step.id, 'group')]]" restamp>
            <katapult-button slot="current-step-body" on-click="nextStep" class="dialog-option" style="margin-left: 16px"
              >Skip</katapult-button
            >
            <paper-table light-grey style="width: 400px;" rounded outline slot="body">
              <paper-row input>
                <paper-cell>
                  <katapult-drop-down
                    items="[[attributeGroups]]"
                    label-function="[[camelCaseKey]]"
                    value-path="$key"
                    label="Select an Attribute Group"
                    value="{{_itemData.grouping}}"
                    no-label-float
                    disabled="[[!canEditAttributeGroup]]"
                  >
                    <paper-item on-click="editAttributeGroups">EDIT ATTRIBUTE GROUPS</paper-item>
                  </katapult-drop-down>
                </paper-cell>
              </paper-row>
            </paper-table>
          </template>

          <template is="dom-if" if="[[equal(step.id, 'tags')]]" restamp>
            <katapult-button slot="current-step-body" on-click="nextStep" class="dialog-option" style="margin-left: 16px"
              >Skip</katapult-button
            >
            <paper-table light-grey style="width: 400px;" rounded outline slot="body">
              <paper-row input>
                <paper-cell>
                  <model-item-tag-selector
                    style="width: -webkit-fill-available;"
                    model-path="[[modelSpaceDetails.path]]"
                    modelCategory="attributes"
                    item-key="[[itemKey]]"
                    tags="[[_itemData.tags]]"
                    on-change="tagSelectorChangeHandler"
                    mayAddTags
                    look-like-paper-element
                  ></model-item-tag-selector>
                </paper-cell>
              </paper-row>
            </paper-table>
          </template>

          <template is="dom-if" if="[[equal(step.id, 'read_only')]]" restamp>
            <paper-cell icon slot="header">
              <paper-checkbox disabled="[[!fullyEditable]]" checked="{{_itemData.read_only}}"></paper-checkbox>
            </paper-cell>
            <katapult-button
              noBorder
              color="var(--secondary-color)"
              slot="current-step-body"
              on-click="buttonSetTrue"
              path="_itemData.read_only"
              class="dialog-option"
              style="margin-right: 8px"
              >Yes</katapult-button
            >
            <katapult-button
              color="var(--secondary-color)"
              slot="current-step-body"
              on-click="buttonSetFalse"
              path="_itemData.read_only"
              class="dialog-option"
              >No</katapult-button
            >
          </template>

          <template is="dom-if" if="[[equal(step.id, 'hide')]]" restamp>
            <paper-cell icon slot="header">
              <paper-checkbox disabled="[[!fullyEditable]]" checked="{{hideAttribute}}"></paper-checkbox>
            </paper-cell>
            <katapult-button
              color="var(--secondary-color)"
              slot="current-step-body"
              on-click="buttonSetHideAttribute"
              confirm
              class="dialog-option"
              style="margin-right: 8px"
              >Yes</katapult-button
            >
            <katapult-button color="var(--secondary-color)" slot="current-step-body" on-click="buttonSetHideAttribute" class="dialog-option"
              >No</katapult-button
            >
          </template>

          <template is="dom-if" if="[[equal(step.id, 'priority')]]" restamp>
            <katapult-button slot="current-step-body" on-click="nextStep" class="dialog-option" style="margin-left: 16px"
              >Skip</katapult-button
            >
            <paper-table light-grey style="width: 200px;" rounded outline slot="body">
              <paper-row input>
                <paper-cell>
                  <paper-input
                    label="Sort Priority"
                    type="number"
                    value="{{_itemData.priority}}"
                    no-label-float
                    disabled="[[!canEditSortOrder]]"
                  ></paper-input>
                </paper-cell>
              </paper-row>
            </paper-table>
          </template>

          <template is="dom-if" if="[[equal(step.id, 'prompt_on_job_creation')]]" restamp>
            <paper-cell icon slot="header">
              <paper-checkbox checked="{{_itemData.show_on_create}}" disabled="[[!fullyEditable]]"></paper-checkbox>
            </paper-cell>
            <template is="dom-if" if="[[!_itemData.show_on_create]]">
              <katapult-button
                color="var(--secondary-color)"
                slot="current-step-body"
                on-click="buttonSetTrue"
                path="_itemData.show_on_create"
                class="dialog-option"
                style="margin-right: 8px"
                >Yes</katapult-button
              >
              <katapult-button
                color="var(--secondary-color)"
                slot="current-step-body"
                on-click="buttonSetFalse"
                path="_itemData.show_on_create"
                class="dialog-option"
                >No</katapult-button
              >
            </template>
            <template is="dom-if" if="[[_itemData.show_on_create]]">
              <paper-table light-grey style="width: 600px;" outline rounded dividers slot="body">
                <paper-row checkbox pointer>
                  <paper-cell> Require this attribute </paper-cell>
                  <paper-cell icon>
                    <paper-checkbox checked="{{_itemData.required_on_create}}"></paper-checkbox>
                  </paper-cell>
                </paper-row>
                <paper-row input>
                  <paper-cell>What order should this attribute show in the job creation dialog?</paper-cell>
                  <paper-cell>
                    <paper-input type="number" label="Create Order" value="{{_itemData.order}}" no-label-float></paper-input>
                  </paper-cell>
                </paper-row>
                <paper-row input>
                  <paper-cell>Should this attribute have a default value?</paper-cell>
                  <paper-cell>
                    <paper-input label="Default Value" value="{{_itemData.default_on_create}}" no-label-float></paper-input>
                  </paper-cell>
                </paper-row>
              </paper-table>
            </template>
          </template>

          <template is="dom-if" if="[[equal(step.id, 'picklist')]]" restamp>
            <katapult-button on-click="nextStep" slot="current-step-header">Continue</katapult-button>
            <bind-path base="{{_itemData.picklists}}" path="[[selectedPicklistKey]]" value="{{picklistItems}}"></bind-path>
            <div slot="body" style="flex-grow: 1;">
              <template is="dom-if" if="[[showWarning(_itemData)]]">
                <div style="margin-bottom: 16px;">
                  Please ensure you add new values to the correct picklist. These picklists are used in various places in the platform to
                  filter data and placing your new value in the appropriate picklist will ensure the filtering works correctly.
                </div>
              </template>
              <paper-table id="picklist" rounded outline white style="overflow: hidden;">
                <paper-row grey divider>
                  <template is="dom-if" if="[[picklistKeys.length]]">
                    <paper-tabs
                      style$="flex-grow: [[picklistKeys.length]];"
                      selected="{{selectedPicklistIndex}}"
                      on-iron-activate="picklistIronActivateEvent"
                      scrollable
                    >
                      <template is="dom-repeat" items="[[picklistKeys]]">
                        <paper-tab>[[camelCase(item)]]</paper-tab>
                      </template>
                    </paper-tabs>
                  </template>
                  <paper-cell
                    style="min-width: fit-content; color: var(--paper-grey-600);"
                    on-click="promptCreatePicklist"
                    pointer
                    ripple
                    center
                  >
                    <span>ADD PICKLIST</span>
                    <iron-icon style="margin-left: 8px;" icon="av:playlist-add"></iron-icon>
                  </paper-cell>
                </paper-row>
                <input id="imageInput" style="display:none;" type="file" accept="image/*" on-change="imageFileSelected" />
                <paper-table-scroll on-sort-changed="picklistItemsSortChanged">
                  <template is="dom-repeat" items="{{picklistItems}}">
                    <paper-header-content
                      white
                      opened="{{item.$opened}}"
                      no-click-row
                      slot="[[picklistItemsSlot]]"
                      divider
                      no-animation="[[disablePicklistAnimation]]"
                    >
                      <template is="dom-if" if="[[picklistItemsEditable]]">
                        <paper-cell drag-handle slot="header" icon>
                          <iron-icon icon="katapult-misc:drag-indicator"></iron-icon>
                        </paper-cell>
                      </template>
                      <template is="dom-if" if="[[and(picklistItemsEditable, mayEditEditability)]]">
                        <paper-cell icon-button slot="header">
                          <katapult-button
                            iconOnly
                            noBorder
                            on-click="togglePicklistItemEditability"
                            icon="[[calcPicklistItemEditabilityIcon(item.locked)]]"
                            text-color="[[calcPicklistItemEditabilityIconColor(item.locked)]]"
                          ></katapult-button>
                        </paper-cell>
                      </template>
                      <template is="dom-if" if="[[showPicklistItemsLockedColumn]]">
                        <paper-cell icon slot="header">
                          <template is="dom-if" if="[[item.locked]]">
                            <template is="dom-if" if="[[picklistItemsEditable]]">
                              <iron-icon icon="lock" style="color: var(--paper-red-500);"></iron-icon>
                            </template>
                            <template is="dom-if" if="[[!picklistItemsEditable]]">
                              <iron-icon icon="lock"></iron-icon>
                            </template>
                          </template>
                        </paper-cell>
                      </template>
                      <paper-cell slot="header">
                        <paper-input label="Value" value="{{item.value}}" no-label-float disabled="[[!item.$editable]]"></paper-input>
                      </paper-cell>
                      <paper-cell slot="header">
                        <paper-input
                          label="Nickname"
                          value="{{item.shortname}}"
                          no-label-float
                          disabled="[[!item.$editable]]"
                        ></paper-input>
                      </paper-cell>
                      <paper-cell icon-button slot="header-end">
                        <template is="dom-if" if="[[item.$editable]]">
                          <katapult-button iconOnly noBorder icon="delete" on-click="deletePicklistItem"></katapult-button>
                        </template>
                      </paper-cell>
                      <div style="background: var(--paper-grey-50); padding: 24px;" slot="content">
                        <paper-input label="Description" value="{{item.description}}"></paper-input>
                        <template is="dom-if" if="[[haveFile(item, item.file, item.icons.length)]]">
                          <div style="display:flex; margin-top:20px;">
                            <katapult-button style="flex-grow:1;" color="var(--secondary-color)" icon="open_in_new" on-click="openImageFile"
                              >Example Image</katapult-button
                            >
                            <katapult-button
                              iconOnly
                              noBorder
                              id="removeImageFile"
                              icon="delete"
                              on-click="removeImageFile"
                            ></katapult-button>
                            <paper-tooltip for="removeImageFile">Remove Example Image</paper-tooltip>
                          </div>
                        </template>
                        <div style="display: flex;">
                          <template is="dom-if" if="[[!haveFile(item, item.file, item.icons.length)]]">
                            <katapult-button
                              style="max-width:350px; margin:20px;"
                              color="var(--secondary-color)"
                              icon="add_photo_alternate"
                              on-click="openImageInput"
                              >Upload Example Image</katapult-button
                            >
                          </template>
                          <paper-checkbox
                            style="margin: 24px 0; align-self: center;"
                            checked="[[!equal(item._call, null)]]"
                            on-click="togglePicklistItemAdditionalAttributes"
                            >Add additional attributes when chosen</paper-checkbox
                          >
                        </div>
                        <template is="dom-if" if="[[!equal(item._call, null)]]" restamp>
                          <attribute-table
                            attributes="{{item._call}}"
                            other-attributes="[[otherAttributes]]"
                            model-space-details="[[modelSpaceDetails]]"
                          ></attribute-table>
                        </template>
                      </div>
                    </paper-header-content>
                  </template>
                  <template is="dom-if" if="[[picklistItemsEditable]]">
                    <template is="dom-if" if="[[isArray(picklistItems)]]">
                      <paper-row ripple short slot="after" on-click="addPicklistItem">
                        <paper-cell center pointer style="color: var(--paper-grey-600);">
                          <span>ADD ITEM</span>
                          <iron-icon style="margin-left: 8px;" icon="add-circle-outline"></iron-icon>
                        </paper-cell>
                      </paper-row>
                    </template>
                  </template>
                </paper-table-scroll>
              </paper-table>
            </div>
          </template>

          <template is="dom-if" if="[[equal(step.id, 'group_items')]]" restamp>
            <katapult-button on-click="nextStep" slot="current-step-header">Continue</katapult-button>
            <div style="display: flex; flex-direction: column; flex-grow: 1;" slot="body">
              <paper-table style="width: fit-content;" white rounded outline dividers>
                <paper-row top-rounded short grey>
                  <paper-cell icon>
                    <iron-icon icon="swap-vert"></iron-icon>
                  </paper-cell>
                  <paper-cell>Attribute</paper-cell>
                  <paper-cell>Value</paper-cell>
                  <paper-cell icon></paper-cell>
                </paper-row>
                <paper-table-scroll on-sort-changed="groupItemsSortChanged">
                  <template is="dom-repeat" items="{{_itemData.group_items}}">
                    <paper-row expand white slot="sortable" divider>
                      <paper-cell icon drag-handle>
                        <iron-icon icon="katapult-misc:drag-indicator"></iron-icon>
                      </paper-cell>
                      <paper-cell>
                        <katapult-drop-down
                          label="Select an Attribute"
                          items="[[otherAttributes]]"
                          label-function="[[camelCaseKey]]"
                          value-path="$key"
                          value="{{item.attribute}}"
                          on-selected-changed="groupItemsAttributeChanged"
                          no-label-float
                        >
                          <paper-item on-click="createNewAttribute">CREATE NEW ATTRIBUTE</paper-item>
                        </katapult-drop-down>
                      </paper-cell>
                      <paper-cell>
                        <input-element
                          style="flex-grow: 1;"
                          attribute_name="[[item.attribute]]"
                          hide-attribute-name
                          value="{{item.value}}"
                          model="[[getSub(otherAttributes, item.attribute)]]"
                          other-attributes="[[otherAttributes]]"
                          user-group="[[userGroup]]"
                          no-label-float
                          job-creator="[[modelSpaceDetails.$key]]"
                        ></input-element>
                      </paper-cell>
                      <paper-cell icon-button on-click="deleteGroupItem">
                        <katapult-button iconOnly noBorder icon="delete"></katapult-button>
                      </paper-cell>
                    </paper-row>
                  </template>
                </paper-table-scroll>
                <paper-row ripple short bottom-rounded on-click="addGroupItem">
                  <paper-cell center pointer style="color: var(--paper-grey-600);">
                    <span>ADD ATTRIBUTE</span>
                    <iron-icon style="margin-left: 8px;" icon="add-circle-outline"></iron-icon>
                  </paper-cell>
                </paper-row>
              </paper-table>
            </div>
          </template>

          <template is="dom-if" if="[[equal(step.id, 'allow_duplicates')]]" restamp>
            <paper-cell icon slot="header">
              <paper-checkbox disabled="[[!fullyEditable]]" checked="{{_itemData.allow_duplicates}}"></paper-checkbox>
            </paper-cell>
            <katapult-button
              color="var(--secondary-color)"
              slot="current-step-body"
              on-click="buttonSetTrue"
              path="_itemData.allow_duplicates"
              class="dialog-option"
              style="margin-right: 8px"
              >Yes</katapult-button
            >
            <katapult-button
              color="var(--secondary-color)"
              slot="current-step-body"
              on-click="buttonSetFalse"
              path="_itemData.allow_duplicates"
              class="dialog-option"
              >No</katapult-button
            >
          </template>

          <template is="dom-if" if="[[equal(step.id, 'display_vertically')]]" restamp>
            <paper-cell icon slot="header">
              <paper-checkbox disabled="[[!fullyEditable]]" checked="{{_itemData.display_vertically}}"></paper-checkbox>
            </paper-cell>
            <katapult-button
              color="var(--secondary-color)"
              slot="current-step-body"
              on-click="buttonSetTrue"
              path="_itemData.display_vertically"
              class="dialog-option"
              style="margin-right: 8px"
              >Yes</katapult-button
            >
            <katapult-button
              color="var(--secondary-color)"
              slot="current-step-body"
              on-click="buttonSetFalse"
              path="_itemData.display_vertically"
              class="dialog-option"
              >No</katapult-button
            >
          </template>

          <template is="dom-if" if="[[equal(step.id, 'table')]]" restamp>
            <katapult-button on-click="nextStep" slot="current-step-header">Continue</katapult-button>
            <div style="display: flex; flex-direction: column; flex-grow: 1;" slot="body">
              <paper-table style="width: fit-content;" white rounded outline dividers>
                <paper-row checkbox pointer>
                  <paper-cell icon>
                    <paper-checkbox checked="{{_itemData.show_duplicate}}"></paper-checkbox>
                  </paper-cell>
                  <paper-cell style="white-space: nowrap;">Show Option to Duplicate</paper-cell>
                  <template is="dom-if" if="[[_itemData.combine_rows]]">
                    <paper-cell style="min-width: 280px;"></paper-cell>
                  </template>
                </paper-row>
                <paper-row checkbox pointer>
                  <paper-cell icon>
                    <paper-checkbox checked="{{_itemData.show_duplicate_increment}}"></paper-checkbox>
                  </paper-cell>
                  <paper-cell style="white-space: nowrap;">Show Option to Duplicate and Increment</paper-cell>
                  <template is="dom-if" if="[[_itemData.combine_rows]]">
                    <paper-cell style="min-width: 280px;"></paper-cell>
                  </template>
                </paper-row>
                <paper-row checkbox pointer>
                  <paper-cell icon>
                    <paper-checkbox checked="{{_itemData.combine_rows}}"></paper-checkbox>
                  </paper-cell>
                  <paper-cell style="white-space: nowrap;">Combine Similar Rows</paper-cell>
                  <template is="dom-if" if="[[_itemData.combine_rows]]">
                    <paper-cell style="min-width: 280px;">
                      <katapult-drop-down
                        items="[[counterAttributeOptions]]"
                        label="Quantity Counter Attribute"
                        value="{{_itemData.counter_attribute}}"
                        label-path="$label"
                        value-path="$key"
                        no-label-float
                      ></katapult-drop-down>
                    </paper-cell>
                  </template>
                </paper-row>
                <paper-row checkbox pointer>
                  <paper-cell icon>
                    <paper-checkbox checked="{{_itemData.hide_header}}"></paper-checkbox>
                  </paper-cell>
                  <paper-cell style="white-space: nowrap;">Hide Header Rows</paper-cell>
                </paper-row>
                <paper-row checkbox pointer>
                  <paper-cell icon>
                    <paper-checkbox checked="{{_itemData.hide_add_row}}"></paper-checkbox>
                  </paper-cell>
                  <paper-cell style="white-space: nowrap;">Hide Add New Rows</paper-cell>
                </paper-row>
                <paper-row checkbox pointer>
                  <paper-cell icon>
                    <paper-checkbox checked="{{_itemData.allow_scroll}}"></paper-checkbox>
                  </paper-cell>
                  <paper-cell style="white-space: nowrap;">Allow Horizontal Scroll</paper-cell>
                </paper-row>
                <template is="dom-if" if="{{_itemData.allow_scroll}}">
                  <paper-row>
                    <paper-cell style="white-space: nowrap;">
                      <paper-input
                        type="number"
                        label="Table Pixel Width"
                        min="400"
                        max="3000"
                        placeholder="400 - 3000"
                        required
                        auto-validate
                        value="{{_itemData.table_width}}"
                        style="margin-bottom: 15px;"
                      >
                      </paper-input>
                    </paper-cell>
                  </paper-row>
                </template>
              </paper-table>
              <paper-table style="margin-top: 24px;" white outline rounded>
                <paper-row top-rounded short grey>
                  <paper-cell icon>
                    <iron-icon icon="swap-vert"></iron-icon>
                  </paper-cell>
                  <paper-cell>Attribute</paper-cell>
                  <paper-cell>Value</paper-cell>
                  <div>
                    <paper-cell center style="max-width: 72px;">Display</paper-cell>
                    <paper-tooltip>Include this attribute in the item's label</paper-tooltip>
                  </div>
                  <div>
                    <paper-cell center style="max-width: 72px;">Required</paper-cell>
                    <paper-tooltip>This field should be required on data entry</paper-tooltip>
                  </div>
                  <template is="dom-if" if="[[_itemData.show_duplicate_increment]]">
                    <div>
                      <paper-cell icon>+1</paper-cell>
                      <paper-tooltip>Mark this field as incrementable for duplication (alphabetical or numerical)</paper-tooltip>
                    </div>
                  </template>
                  <paper-cell icon></paper-cell>
                </paper-row>
                <paper-table-scroll on-sort-changed="tableItemsSortChanged">
                  <template is="dom-repeat" items="{{_itemData.table_items}}">
                    <paper-row expand white slot="sortable" divider>
                      <paper-cell icon drag-handle>
                        <iron-icon icon="katapult-misc:drag-indicator"></iron-icon>
                      </paper-cell>
                      <paper-cell>
                        <katapult-drop-down
                          label="Select an Attribute"
                          items="[[tableAttributeList]]"
                          label-function="[[camelCaseKey]]"
                          value-path="$key"
                          value="{{item.attribute}}"
                          no-label-float
                        >
                          <paper-item on-click="createNewAttribute">CREATE NEW ATTRIBUTE</paper-item>
                        </katapult-drop-down>
                      </paper-cell>
                      <paper-cell>
                        <input-element
                          style="flex-grow: 1;"
                          attribute_name="[[item.attribute]]"
                          hide-attribute-name
                          value="{{item.value}}"
                          model="[[getSub(otherAttributes, item.attribute)]]"
                          other-attributes="[[otherAttributes]]"
                          user-group="[[userGroup]]"
                          no-label-float
                          job-creator="[[modelSpaceDetails.$key]]"
                        ></input-element>
                      </paper-cell>
                      <paper-cell center style="max-width: 72px;">
                        <paper-checkbox checked="{{item.display}}"></paper-checkbox>
                      </paper-cell>
                      <paper-cell center style="max-width: 72px;">
                        <paper-checkbox checked="{{item.required}}"></paper-checkbox>
                      </paper-cell>
                      <template is="dom-if" if="[[_itemData.show_duplicate_increment]]">
                        <paper-cell icon>
                          <paper-checkbox checked="{{item.incrementable}}"></paper-checkbox>
                        </paper-cell>
                      </template>
                      <paper-cell icon-button on-click="deleteTableItem">
                        <katapult-button iconOnly noBorder icon="delete"></katapult-button>
                      </paper-cell>
                    </paper-row>
                  </template>
                </paper-table-scroll>
                <paper-row ripple short bottom-rounded on-click="addTableItem">
                  <paper-cell center pointer style="color: var(--paper-grey-600);">
                    <span>ADD ATTRIBUTE</span>
                    <iron-icon style="margin-left: 8px;" icon="add-circle-outline"></iron-icon>
                  </paper-cell>
                </paper-row>
              </paper-table>
            </div>
          </template>

          <template is="dom-if" if="[[equal(step.id, 'single_line_text')]]" restamp>
            <paper-cell icon slot="header"></paper-cell>
            <div slot="body" style="display: flex; flex-direction: column; gap: 16px;">
              <div style="display: flex; align-items: center; gap: 8px;">
                <div>Validate inputs:</div>
                <paper-radio-group
                  selected="{{_itemData.validation.validation_context}}"
                  name="validation_context"
                  on-selected-changed="validationInputChanged"
                >
                  <paper-radio-button name="EVERYWHERE">Everywhere</paper-radio-button>
                  <paper-radio-button name="MOBILE_ASSESSMENTS">Only In Mobile Assessments</paper-radio-button>
                  <paper-radio-button name="NEVER">Do Not Validate</paper-radio-button>
                </paper-radio-group>
              </div>
              <div style="display: flex; flex-direction: column; gap: 16px; padding-left: 24px;">
                <paper-input
                  disabled="[[!_isValidationInputEnabled(_itemData.validation.validation_context, fullyEditable)]]"
                  on-change="validationInputChanged"
                  name="regex_string"
                  value="{{_itemData.validation.regex_string}}"
                  label="Regex Expression"
                ></paper-input>
                <div class="validation-regex-builder">
                  <p style="margin: 0; font-weight: bold;">Quick Regex Builder</p>
                  <div>
                    Use these inputs to quickly build a regular expression validation string. This will overwrite the "Regex Expression"
                    input above. For more complex regex expressions, use the input above directly.
                  </div>
                  <paper-checkbox
                    style="padding: 8px 0;"
                    disabled="[[!_isValidationInputEnabled(_itemData.validation.validation_context, fullyEditable)]]"
                    on-checked-changed="validationInputChanged"
                    name="numeric_only"
                    checked="{{_itemData.validation.numeric_only}}"
                    >Numeric Input Only</paper-checkbox
                  >
                  <template is="dom-if" if="[[_itemData.validation.numeric_only]]">
                    <paper-checkbox
                      style="padding: 0 0 0 43px;"
                      disabled="[[!_isValidationInputEnabled(_itemData.validation.validation_context, fullyEditable)]]"
                      on-checked-changed="validationInputChanged"
                      name="allow_negative"
                      checked="{{_itemData.validation.allow_negative}}"
                      >Allow Negative</paper-checkbox
                    >
                  </template>
                  <div style="display: flex; flex-direction: column; gap: 8px;">
                    <div
                      class="input-label"
                      disabled$="[[!_isValidationInputEnabled(_itemData.validation.validation_context, fullyEditable)]]"
                    >
                      Limit character count to:
                    </div>
                    <div style="display: flex; flex-direction: column;">
                      <div style="display: flex; align-items: center; gap: 16px; padding-left: 24px;">
                        <paper-radio-button
                          name="max_length"
                          disabled="[[!_isValidationInputEnabled(_itemData.validation.validation_context, fullyEditable)]]"
                          checked="[[!_isNull(_itemData.validation.max_length)]]"
                          on-click="validationCharLimitOptionChanged"
                          >Maximum Number of Characters</paper-radio-button
                        >
                        <paper-input
                          disabled="[[!_isCharLimitInputEnabled(_itemData.validation.max_length, _itemData.validation.validation_context, fullyEditable)]]"
                          no-label-float
                          on-change="validationInputChanged"
                          type="number"
                          min="1"
                          placeholder="e.g. 200"
                          name="max_length"
                          value="{{_itemData.validation.max_length}}"
                        ></paper-input>
                      </div>
                      <div style="display: flex; align-items: center; gap: 16px; padding-left: 24px;">
                        <paper-radio-button
                          name="exact_length"
                          disabled="[[!_isValidationInputEnabled(_itemData.validation.validation_context, fullyEditable)]]"
                          checked="[[!_isNull(_itemData.validation.exact_length)]]"
                          on-click="validationCharLimitOptionChanged"
                          >Exact Number of Characters</paper-radio-button
                        >
                        <paper-input
                          disabled="[[!_isCharLimitInputEnabled(_itemData.validation.exact_length, _itemData.validation.validation_context, fullyEditable)]]"
                          no-label-float
                          on-change="validationInputChanged"
                          type="number"
                          min="1"
                          placeholder="e.g. 6"
                          name="exact_length"
                          value="{{_itemData.validation.exact_length}}"
                        ></paper-input>
                      </div>
                    </div>
                  </div>
                </div>
                <paper-input
                  disabled="[[!_isValidationInputEnabled(_itemData.validation.validation_context, fullyEditable)]]"
                  on-change="validationInputChanged"
                  name="error_message"
                  value="{{_itemData.validation.error_message}}"
                  label="Custom Error Message"
                ></paper-input>
              </div>
            </div>
          </template>
        </katapult-editor-step>
      </template>
      ${super.createItemContainer}
    `;
  }

  static get properties() {
    return {
      attributeTypes: {
        type: Object,
        value: {
          node: false,
          connection: false,
          section: false,
          photo: false,
          job: false
        }
      },
      null: {
        type: Object,
        value: null
      },
      picklistItems: {
        type: Array
      },
      picklistKeys: {
        type: Array,
        value: []
      },
      selectedPicklistIndex: {
        type: Number,
        value: 0
      },
      catalogsLookup: {
        type: Object,
        $Pcomputed: 'computeCatalogsLookup(catalogsData)'
      },
      editingDisabled: {
        type: Boolean,
        value: false,
        notify: true
      }
    };
  }

  static get observers() {
    return [
      'updateSanitizedPicklistName(newPicklistName)',
      'updateAttributeList(otherAttributes.*)',
      'updateTableAttributeList(otherAttributes.*)',
      'updateItemDataAttributeTypes(attributeTypes.*)',
      'updateAttributeTypes(_itemData.attribute_types)',
      'updateHideAttribute(_itemData.required_permission)',
      'updateItemDataRequiredPermission(hideAttribute)',
      'updatePicklistKeys(_itemData.picklists.*)',
      'inputTypeChanged(_itemData.gui_element)',
      'updateSelectedPicklistKey(selectedPicklistIndex, picklistKeys)',
      'updateEditabilityOptions(_itemData.gui_element)',
      'updateEditabilityFlags(_itemData.editability, userGroup)',
      'updateMayEditEditability(userGroup)',
      'updateShowPicklistItemsLockedColumn(mayEditEditability, picklistItems.splices)',
      'updatePicklistItemEditabilityFlags(picklistItemsEditable, picklistItems.splices, catalogsData)',
      'updatePicklistItemsSlot(picklistItemsEditable)',
      'updateAttributeGroupsArray(attributeGroups.*)',
      'updateCounterAttributeOptions(_itemData.table_items.*)',
      'validationOptionsChanged(_itemData.validation.*)',
      'updateGroupItemsModel(_itemData.group_items)'
    ];
  }

  constructor() {
    super();

    // Template helpers
    this._isValidationInputEnabled = (validationContext, fullyEditable) =>
      ['EVERYWHERE', 'MOBILE_ASSESSMENTS'].includes(validationContext) && fullyEditable;
    this._isCharLimitInputEnabled = (limitTypeValue, validationContext, fullyEditable) =>
      limitTypeValue != null && this._isValidationInputEnabled(validationContext, fullyEditable);
    this._isNull = (value) => value == null;
  }

  ready() {
    super.ready();

    this.keyMatchLabel = true;

    // An ordered list of all possible steps.
    this.setSteps([
      {
        id: 'input_type',
        title: 'Attribute Input Type',
        trigger: () => this.get('_itemData.gui_element') != null
      },
      {
        id: 'placeholder',
        title: 'Placeholder Text',
        trigger: () => this.get('_itemData.placeholder') != null,
        if: () => ['textbox', 'textbox_commit', 'textarea'].includes(this.get('_itemData.gui_element'))
      },
      {
        id: 'may_exist_on',
        title: 'Attribute may be added to',
        trigger: () => Array.isArray(this.get('_itemData.attribute_types')) && this.get('_itemData.attribute_types.length') > 0
      },
      {
        id: 'group',
        title: 'Attribute Group',
        if: () => {
          let attrTypes = this.get('_itemData.attribute_types');
          return Array.isArray(attrTypes) && ['node', 'connection', 'section', 'job'].some((x) => attrTypes.includes(x));
        },
        trigger: () => this.get('_itemData.grouping') != null
      },
      {
        id: 'tags',
        title: 'Tags',
        trigger: () => this.get('_itemData.tags') != null
      },
      {
        id: 'prompt_on_job_creation',
        title: 'Prompt on Job Creation',
        if: () => {
          let attrTypes = this.get('_itemData.attribute_types');
          return Array.isArray(attrTypes) && attrTypes.includes('job');
        },
        trigger: () => this.get('_itemData.show_on_create') != null
      },
      {
        id: 'read_only',
        title: 'Read Only (Not User Editable)',
        trigger: () => this.get('_itemData.read_only') !== undefined
      },
      {
        id: 'hide',
        title: 'Hide Attribute in Read-Only Mode',
        trigger: () => this.get('_itemData.required_permission') !== undefined
      },
      {
        id: 'priority',
        title: 'Attribute Sort Priority',
        trigger: () => this.get('_itemData.priority') != null
      },
      {
        id: 'picklist',
        title: 'Picklists',
        if: () => ['dropdown', 'multi_dropdown'].includes(this.get('_itemData.gui_element'))
      },
      {
        id: 'group_items',
        title: 'Attributes to Add for this Group',
        if: () => this.get('_itemData.gui_element') == 'group',
        trigger: () => false
      },
      {
        id: 'allow_duplicates',
        title: 'Allow Duplicate Selections',
        if: () => this.get('_itemData.gui_element') == 'multi_dropdown',
        trigger: () => this.get('_itemData.allow_duplicates') != null
      },
      {
        id: 'display_vertically',
        title: 'Always Display Vertically (Sortable)',
        if: () => this.get('_itemData.gui_element') == 'multi_dropdown',
        trigger: () => this.get('_itemData.display_vertically') != null
      },
      {
        id: 'table',
        title: 'Table Details',
        if: () => this.get('_itemData.gui_element') == 'table'
      },
      {
        id: 'single_line_text',
        title: 'Validation Options',
        if: () => this.get('_itemData.gui_element') == 'textbox',
        trigger: () => this.get('_itemData.validation') != null
      },
      {
        /* This should be the last step in attribute creation because if this value it set before the end of the attribute 
        creation process, you won't be able to change anything after and therefore wouldn't be able to finish the steps properly */
        id: 'editability',
        title: 'Editability Restriction',
        if: () => this.mayEditEditability,
        trigger: () => this.get('_itemData.editability') != null
      }
    ]);
  }

  addNewAttributeGroup() {
    let sanitized = (this.newAttributeGroupName || '').trim();
    let exists = sanitized.length > 0;
    let illegal = ContainsIllegalCharacters(sanitized);
    let error = !exists
      ? 'Your attribute group must have a name'
      : illegal
        ? `You may not use the following characters: ${IllegalCharacters.join(' ')}`
        : null;
    this.$.newAttributeGroupNameInput.errorMessage = error;
    this.$.newAttributeGroupNameInput.invalid = error != null;
    if (error == null) {
      this.attributeGroupsArray.push({ $key: sanitized });
      this.saveAttributeGroups();
      this.newAttributeGroupName = '';
    }
  }
  async removeAttributeGroup(e) {
    await this.$.attributeGroupsFirebaseWorker._ref.child(e.model.item.$key).remove();
  }

  addPicklistItem() {
    this.push('picklistItems', {});
  }

  addTableItem() {
    if (Array.isArray(this.get('_itemData.table_items'))) this.push('_itemData.table_items', { display: true });
    else this.set('_itemData.table_items', [{ display: true }]);
  }

  attributeGroupsArraySortChanged(e) {
    let removedItem = this.attributeGroupsArray.splice(e.detail.prevIndex, 1)[0];
    this.attributeGroupsArray.splice(e.detail.index, 0, removedItem);
    this.notifySplices(`attributeGroupsArray`, [
      { index: e.detail.prevIndex, removed: [removedItem], addedCount: 0 },
      { index: e.detail.index, removed: [], addedCount: 1 }
    ]);
    this.saveAttributeGroups();
  }

  beforeSave() {
    if (Array.isArray(this.get('_itemData.table_items')))
      this.set(
        '_itemData.table_items',
        this._itemData.table_items.filter((x) => x.attribute)
      );
  }

  buttonSetHideAttribute(e) {
    this.hideAttribute = e.currentTarget.hasAttribute('confirm');
    this.updateItemDataRequiredPermission();
  }

  buttonSetTrue(e) {
    this.set(e.currentTarget.getAttribute('path'), true);
  }

  buttonSetFalse(e) {
    this.set(e.currentTarget.getAttribute('path'), false);
  }

  calcPicklistItemEditable(locked) {
    return (this.picklistItemsEditable && !locked) || this.mayEditEditability;
  }

  calcPicklistItemEditabilityIcon(locked) {
    return locked ? 'lock' : 'lock_open';
  }

  calcPicklistItemEditabilityIconColor(locked) {
    return locked ? 'var(--paper-red-500)' : 'inherit';
  }

  camelCaseAttribute(x) {
    if (x) return CamelCase(x.attribute);
    return '';
  }

  computeCatalogsLookup(catalogsData) {
    let lookup = {};
    for (let catalogKey in catalogsData) {
      if (catalogsData[catalogKey].name) {
        lookup[catalogsData[catalogKey].name] = catalogKey;
      }
    }
    return lookup;
  }

  createPicklistButtonClicked() {
    if (this.sanitizedPicklistName.length) {
      let key = NameToKey(this.sanitizedPicklistName);
      if (this.get('_itemData.picklists') == null) this.set('_itemData.picklists', { [key]: [] });
      else this.set(`_itemData.picklists.${key}`, []);
      this.$.createPicklistDialog.close();
      setTimeout(() => (this.selectedPicklistIndex = this.picklistKeys.length - 1));
    }
  }

  deletePicklistItem(e) {
    this.splice('picklistItems', e.model.index, 1);
  }

  deleteTableItem(e) {
    this.splice('_itemData.table_items', e.model.index, 1);
  }

  editAttributeGroups(e) {
    e.currentTarget.parentElement.close();
    this.$.editAttributeGroups.open();
  }

  inputTypeChanged() {
    // Anytime the input type changes, we backup the data of the previous input type in case the user comes back to it.
    let inputType = this.get('_itemData.gui_element');

    let pathsToClear = [];
    let pathsToRestore = [];

    let registerPaths = (relevant, paths) => (relevant ? pathsToRestore : pathsToClear).push(...paths);

    if (inputType) {
      registerPaths(['textbox', 'textbox_commit', 'textarea'].includes(inputType), ['placeholder']);
      registerPaths(inputType == 'table', [
        'combine_rows',
        'counter_attribute',
        'show_duplicate',
        'show_duplicate_increment',
        'table_items',
        'hide_header',
        'hide_add_row',
        'allow_scroll',
        'table_width'
      ]);
      registerPaths(inputType == 'group', ['group_items']);
      registerPaths(inputType == 'textbox', ['validation']);
    }

    pathsToClear.forEach((path) => {
      let val = Path.get(this._itemData, path);
      if (val != null) Path.set(this._itemData, Path.join(['$backup', path]), val);
      Path.set(this._itemData, path, null);
    });

    pathsToRestore.forEach((path) => {
      if (Path.get(this._itemData, path) == null) {
        let val = Path.get(this._itemData, Path.join(['$backup', path]));
        Path.set(this._itemData, path, val);
      }
    });
  }

  picklistIronActivateEvent(e) {
    if (e.detail.selected >= this.get('picklistKeys.length')) {
      e.preventDefault();
      e.stopPropagation();

      // Create New Picklist.
    }
  }

  picklistItemsSortChanged(e) {
    this.disablePicklistAnimation = true;
    setTimeout(() => {
      let removedItem = this.picklistItems.splice(e.detail.prevIndex, 1)[0];
      this.picklistItems.splice(e.detail.index, 0, removedItem);
      this.notifySplices(`picklistItems`, [
        { index: e.detail.prevIndex, removed: [removedItem], addedCount: 0 },
        { index: e.detail.index, removed: [], addedCount: 1 }
      ]);
      setTimeout(() => (this.disablePicklistAnimation = false));
    });
  }

  promptCreatePicklist() {
    this.newPicklistName = '';
    this.$.createPicklistDialog.open();
  }

  async saveAttributeGroups() {
    let update = {};
    this.attributeGroupsArray.forEach((item, i) => {
      update[item.$key] = { priority: i, '.priority': i };
    });
    await this.$.attributeGroupsFirebaseWorker._ref.update(update);
  }

  async _saveUpdate(itemData, update, detail) {
    // Get a list of the new example photo keys
    let newPhotoKeys = {};
    for (let key in itemData?.picklists) {
      for (let item of itemData.picklists[key]) {
        item?.icons?.forEach((icon) => (newPhotoKeys[icon.photo] = true));
      }
    }

    // Delete old photos that were removed (looping over this.itemData because it is the old copy of the data)
    for (let key in this.itemData?.picklists) {
      for (let item of this.itemData.picklists[key]) {
        if (item?.icons) {
          for (let icon of item.icons) {
            if (icon.photo && !newPhotoKeys[icon.photo]) {
              try {
                await firebase.storage().ref(`company_files/${this.userGroup}/attribute_example_images/${icon.photo}`).delete();
              } catch (e) {
                console.error(e);
              }
            }
          }
        }
      }
    }

    // Upload the file if we have a new one
    for (let key in itemData?.picklists) {
      for (let item of itemData.picklists[key]) {
        if (item.file) {
          let key = item.icons?.find((icon) => icon.photo)?.photo;
          if (key) await firebase.storage().ref(`company_files/${this.userGroup}/attribute_example_images/${key}`).put(item.file);
          delete item.file;
        }
      }
    }

    // Save the item
    await super._saveUpdate(itemData, update, detail);
  }

  tableItemsSortChanged(e) {
    let removedItem = this._itemData.table_items.splice(e.detail.prevIndex, 1)[0];
    this._itemData.table_items.splice(e.detail.index, 0, removedItem);
    this.notifySplices(`_itemData.table_items`, [
      { index: e.detail.prevIndex, removed: [removedItem], addedCount: 0 },
      { index: e.detail.index, removed: [], addedCount: 1 }
    ]);
  }

  addGroupItem() {
    let defaultValue = { attribute: null, value: null };
    if (Array.isArray(this.get('_itemData.group_items'))) this.push('_itemData.group_items', defaultValue);
    else this.set('_itemData.group_items', [defaultValue]);
  }

  groupItemsSortChanged(e) {
    let removedItem = this._itemData.group_items.splice(e.detail.prevIndex, 1)[0];
    this._itemData.group_items.splice(e.detail.index, 0, removedItem);
    this.notifySplices(`_itemData.group_items`, [
      { index: e.detail.prevIndex, removed: [removedItem], addedCount: 0 },
      { index: e.detail.index, removed: [], addedCount: 1 }
    ]);
  }

  deleteGroupItem(e) {
    this.splice('_itemData.group_items', e.model.index, 1);
  }

  updateGroupItemsModel() {
    let groupItems = this.get('_itemData.group_items');
    if (groupItems && this.otherAttributes) {
      // Only update the model if any items in the group_items list is just a string value
      let shouldUpdate = groupItems.some((item) => typeof item === 'string');
      if (shouldUpdate) {
        // Convert any items that are just strings to the object format
        groupItems = groupItems.map((item) => {
          if (typeof item === 'string') return { attribute: item, value: GetNewAttributeValue(item, this.otherAttributes) };
          return item;
        });
        this.set(`_itemData.group_items`, groupItems);
      }
    }
  }

  groupItemsAttributeChanged(e) {
    if (e && e.model && e.detail && !e.detail.value) {
      this.set(`_itemData.group_items.${e.model.index}.value`, null);
    }
  }

  togglePicklistItemAdditionalAttributes(e) {
    // Prevent checkbox from being toggled from click.
    e.currentTarget.checked = !e.currentTarget.checked;
    if (Array.isArray(e.model.get('item._call'))) {
      e.model.set('item.$backup', { _call: e.model.get('item._call') });
      e.model.set('item._call', null);
    } else {
      e.model.set('item._call', e.model.get('item.$backup._call') || []);
    }
  }

  togglePicklistItemEditability(e) {
    e.model.set('item.locked', e.model.get('item.locked') ? null : true);
  }

  updateAttributeGroupsArray() {
    this.attributeGroupsArray = ToArray(this.attributeGroups).sort((a, b) => Path.get(a, 'priority') - Path.get(b, 'priority'));
  }

  updateAttributeList() {
    // this.attributeList = ToArray(this.otherAttributes).map(attr => ({$key: attr.$key, $label: CamelCase(attr.$key)});
  }

  updateTableAttributeList() {
    if (!this.otherAttributes) return;
    let finalObject = {};
    for (const [attributeKey, attributeVal] of Object.entries(this.otherAttributes)) {
      if (['dropdown', 'textbox', 'textarea', 'checkbox'].includes(attributeVal.gui_element)) {
        finalObject[attributeKey] = attributeVal;
      }
    }
    this.tableAttributeList = finalObject;
  }

  updateAttributeTypes() {
    let itemDataAttributeTypes = this.get('_itemData.attribute_types');
    if (!Array.isArray(itemDataAttributeTypes)) itemDataAttributeTypes = [];
    for (let key in this.attributeTypes) this.attributeTypes[key] = itemDataAttributeTypes.includes(key);
    for (let key in this.attributeTypes) this.notifyPath(`attributeTypes.${key}`);
  }

  updateCounterAttributeOptions() {
    let temp = [];
    if (Array.isArray(this.get('_itemData.table_items'))) {
      temp = this._itemData.table_items.map((x) => ({ $key: x.attribute, $label: CamelCase(x.attribute) }));
    }
    this.counterAttributeOptions = temp;
  }

  updateEditabilityFlags() {
    /* Editability options: "uneditable", "only_picklist_items", and none (null)
    A user is only able to fully edit an attribute if it's editability is "editable" or essentially not "uneditable".  A user can edit the editability settings
    if they are within the katapult user group,  but this information is contained within this.mayEditEditability and functions in other methods */
    let editability = this.get('_itemData.editability');
    this.fullyEditable = editability == null || editability == 'editable';
    this.picklistItemsEditable = this.fullyEditable || editability == 'only_picklist_items';

    // Still allow the user to change the sort order and grouping of the attribute even if it's not fully editable
    this.canEditSortOrder = true;
    this.canEditAttributeGroup = true;

    // Updates editingDisabled, which will notify a variable in the model editor workspace to disable / enable the delete button
    if (this.fullyEditable == false) this.set('editingDisabled', true);
    else this.set('editingDisabled', false);
  }

  updateEditabilityOptions() {
    // Add basic editability options.
    let temp = [{ label: 'May Not Edit', value: 'uneditable' }];
    // Add addtional optiosn for dropdowns.
    if (this.get('_itemData.gui_element') == 'dropdown') {
      temp.push({ label: 'May Only Edit Picklist Items', value: 'only_picklist_items' });
    }
    this.editabilityOptions = temp;
  }

  updateHideAttribute() {
    this.hideAttribute = this.get('_itemData.required_permission') === 'write';
  }

  updateItemDataAttributeTypes() {
    if (this.attributeTypes) {
      let temp = ToArray(this.attributeTypes)
        .filter((x) => x.$val)
        .map((x) => x.$key);
      this.set('_itemData.attribute_types', temp);
    }
  }

  updateItemDataRequiredPermission() {
    this.set('_itemData.required_permission', this.hideAttribute ? 'write' : null);
  }

  updateMayEditEditability() {
    this.mayEditEditability = this.userGroup == 'katapult';
  }

  updatePicklistItemEditabilityFlags(one, two, three) {
    // Get the catagory for the attribute
    if (this._itemData) {
      let itemCatalog = this.get(`catalogsLookup.${this._itemData.category}`);
      let catalogPicklist = this.get(
        `catalogsData.${itemCatalog}.models.attributes.${this._itemData.$key}.picklists.${this.selectedPicklistKey}`
      );
      if (Array.isArray(this.picklistItems))
        this.picklistItems.forEach((item, i) => {
          // Assume the picklist item is not a default
          let itemIsCatalogDefault = false;
          if (catalogPicklist) {
            // Check if the picklist item is a default for the attribute in the catalog
            itemIsCatalogDefault = Object.values(catalogPicklist).find((x) => x.value == item.value) ? true : false;
          }
          this.set(`picklistItems.${i}.$editable`, this.picklistItemsEditable && !item.locked && !itemIsCatalogDefault);
        });
    }
  }

  updatePicklistItemsSlot() {
    this.picklistItemsSlot = this.picklistItemsEditable ? 'sortable' : 'before';
  }

  updatePicklistKeys() {
    let picklistKeys = Object.keys(this.get('_itemData.picklists') || {});
    if (!this.picklistKeys.length) this.selectedPicklistIndex = picklistKeys.length ? 0 : null;
    this.picklistKeys = picklistKeys;
  }

  updateSanitizedPicklistName() {
    this.sanitizedPicklistName = (this.newPicklistName || '').trim();
  }

  updateSelectedPicklistKey() {
    this.selectedPicklistKey = this.get(`picklistKeys.${this.selectedPicklistIndex}`);
  }

  updateShowPicklistItemsLockedColumn() {
    this.showPicklistItemsLockedColumn =
      !this.mayEditEditability && Array.isArray(this.picklistItems) && this.picklistItems.some((item) => item.locked);
  }

  validationOptionsChanged(changeRecord) {
    // only run this update function when you change the available options
    if (this._itemData && !changeRecord.path.includes('regex_string') && changeRecord.path != '_itemData.validation') {
      if (!this._itemData.validation) this._itemData.validation = {};
      let validation = this._itemData.validation;
      let regexParts = {
        allowedCharacters: '\\S',
        lengthModifier: '*'
      };

      if (validation.numeric_only) {
        regexParts.allowedCharacters = '\\d';
      }
      if (validation.max_length) {
        regexParts.lengthModifier = `{0,${validation.max_length}}`;
      }
      if (validation.exact_length) {
        regexParts.lengthModifier = `{${validation.exact_length}}`;
      }
      let regexString = '';
      if (validation.numeric_only && validation.allow_negative) {
        if (regexParts.lengthModifier == '*') regexParts.lengthModifier = '';
        else regexParts.lengthModifier = `(?=.${regexParts.lengthModifier}$)`;
        regexString = `^${regexParts.lengthModifier}(-?${regexParts.allowedCharacters}+)?$`;
      } else regexString = `^${regexParts.allowedCharacters}${regexParts.lengthModifier}$`;
      this.set('_itemData.validation.regex_string', regexString);
    }

    // If there are validation options, but no validation context, set it to the historical default
    if (this._itemData?.validation && this._itemData.validation.validation_context == null) {
      this.set('_itemData.validation.validation_context', 'MOBILE_ASSESSMENTS');
    }
  }

  // TODO (2024-07-15): It would be better UX for manual changes to the regex string to reflect in the other fields (mainly unsetting them)
  validationInputChanged(e) {
    const target = e.target;
    const validationProperty = target?.getAttribute('name');
    if (!this._itemData || !validationProperty) return;

    let val = target.value;
    // If "validation" object doesn't exist, create it
    if (!this._itemData.validation) this._itemData.validation = {};
    // use checkbox value instead
    if (validationProperty == 'numeric_only') val = target.checked;
    // update validation attribute
    if (val) this.set(`_itemData.validation.${validationProperty}`, val); // TODO (2024-07-15): Bug? If val is false, it will not be set
  }

  validationCharLimitOptionChanged(e) {
    const target = e.target;
    const limitType = target?.getAttribute('name');
    if (!this._itemData || !limitType) return;

    // "Enable" the text input for the selected limit type
    this.set(`_itemData.validation.${limitType}`, target.checked ? '' : null);

    // If we are enabling the first limit, "disable" the input and uncheck the radio button for the other limit
    if (target.checked) {
      const otherLimitType = limitType == 'max_length' ? 'exact_length' : 'max_length';
      this.set(`_itemData.validation.${otherLimitType}`, null);
    }
  }

  // updatePicklistItems() {
  //   let items = this.get(`_itemData.picklists.${this.get(`picklistKeys.${this.selectedPicklistIndex}`)}`);
  //   if (Array.isArray(items)) this.picklistItems = items;
  //   else this.picklistItems = [];
  // }

  haveFile(itemData) {
    return itemData?.file || itemData?.icons?.find((icon) => icon.photo);
  }

  openImageInput(e) {
    this.imageInputIndex = e.model.index;
    this.shadowRoot.querySelector('#imageInput').click();
  }

  async imageFileSelected(e) {
    let file = e.target.files[0];
    if (file && this.imageInputIndex != null) {
      this.set(`picklistItems.${this.imageInputIndex}.file`, file);
      this.set(`picklistItems.${this.imageInputIndex}.icons`, [{ icon: 'image:photo', photo: FirebaseWorker.ref().push().key, size: 24 }]);
    }
    this.imageInputIndex = null;
    e.currentTarget.value = null;
  }

  async openImageFile(e) {
    let url = null;
    // Get the picklist item
    let item = this.get(`picklistItems.${e.model.index}`) ?? {};
    // Handle local files not yet uploaded
    if (item.file) {
      url = URL.createObjectURL(item.file);
    } else {
      // Handle files stored in firebase storage
      let photoKey = item.icons?.find((icon) => icon.photo)?.photo;
      if (photoKey) {
        url = await firebase.storage().ref(`company_files/${this.userGroup}/attribute_example_images/${photoKey}`).getDownloadURL();
      }
    }

    // Open it in a new window
    if (url) window.open(url);
  }

  removeImageFile(e) {
    this.set(`picklistItems.${e.model.index}.file`, null);
    let item = this.get(`picklistItems.${e.model.index}`) ?? {};
    let photoIndex = item.icons?.findIndex((icon) => icon.photo);
    if (photoIndex != null && photoIndex != -1) {
      this.splice(`picklistItems.${e.model.index}.icons`, photoIndex, 1);
    }
  }
  showWarning(item) {
    const attributesToWarn = ['company', 'cable_type'];
    const shouldWarn = attributesToWarn.includes(item?.$key);
    return shouldWarn;
  }
  tagSelectorChangeHandler(e) {
    const tags = e.target.tags;
    this.set('_itemData.tags', tags);
  }
}

customElements.define('katapult-attribute-editor', KatapultAttributeEditor);
